<template>
  <el-dialog v-model="currentAttachment" width="80%">
    <div class="flex justify-center">
      <img v-if="isImage(currentAttachment?.filename)" class="el-upload-list__item-thumbnail"
           :src="settingsStore.storagePath + '/users/' +
                     employee?.id + '/' + currentAttachment.filename"
           alt="" width="80%">
      <embed v-if="isPDF(currentAttachment?.filename)" :src="settingsStore.storagePath + '/users/' +
                     employee?.id + '/' + currentAttachment.filename" width="100%" height="600px"/>
    </div>
  </el-dialog>

  <div class="flex flex-col gap-4">
    <ul class="flex flex-wrap el-upload-list el-upload-list--picture-card">
      <!-- Existing saved attachments -->
      <li :key="i"
          v-for="(userAttachment, i) in employee?.userAttachments"
          class="el-upload-list__item">
        <div class="w-[148px]">
          <template
              v-if="userAttachment.mimetype == 'image/png' || userAttachment.mimetype == 'image/jpeg'">
            <img class="el-upload-list__item-thumbnail"
                 :src="settingsStore.storagePath + '/users/' +
                     employee?.id + '/' + userAttachment.filename"
                 alt="">
          </template>
          <template v-else>
            <div class="w-full h-full flex items-center justify-center">
              <el-icon style="font-size: 48px">
                <Document/>
              </el-icon>
            </div>
          </template>

          <label
              class="absolute left-[50%] translate-x-[-50%] bottom-0 bg-black/45 text-white p-1 w-[100%] truncate">
            {{ userAttachment.originalFilename }}
          </label>
          <span class="el-upload-list__item-actions">
             <span v-if="canBePreviewed(userAttachment.filename)" class="el-upload-list__item-preview">
               <el-icon @click="() => { currentAttachment = userAttachment }">
                <Icon icon="icon-park-outline:preview-open"/>
              </el-icon>
             </span>
            <span class="el-upload-list__item-preview">
               <el-icon @click="() => { downloadAttachment(userAttachment) }">
                <Download/>
              </el-icon>
             </span>
            <span class="el-upload-list__item-delete">
               <el-icon @click="() => { handleRemoveOnRemote(userAttachment) }">
                <Delete/>
              </el-icon>
            </span>
          </span>
        </div>
      </li>

      <!-- Staged (pending) attachments — shown before save on create -->
      <li v-for="(file, i) in props.modelValue" :key="'pending-' + i"
          class="el-upload-list__item opacity-60">
        <div class="w-[148px]">
          <template v-if="file.type === 'image/png' || file.type === 'image/jpeg'">
            <img class="el-upload-list__item-thumbnail"
                 :src="getStagedPreviewUrl(file)" alt="">
          </template>
          <template v-else>
            <div class="w-full h-full flex items-center justify-center">
              <el-icon style="font-size: 48px">
                <Document/>
              </el-icon>
            </div>
          </template>

          <label class="absolute left-[50%] translate-x-[-50%] bottom-0 bg-black/45 text-white p-1 w-[100%] truncate">
            {{ file.name }}
          </label>

          <span class="el-upload-list__item-actions">
            <span class="el-upload-list__item-delete">
              <el-icon @click="() => { removeStagedFile(i) }">
                <Delete/>
              </el-icon>
            </span>
          </span>
        </div>
      </li>

      <!-- Upload button -->
      <li>
        <el-upload
            class="w-[146px] h-[147px]"
            drag
            multiple
            :show-file-list="false"
            :action="employee?.id ? `${User.ENDPOINT}/${employee.id}/attachments` : ''"
            :before-upload="beforeUpload"
            :on-success="onSuccess"
            :on-error="showError"
            :http-request="handleUpload"
        >
          <div class="m-[17px] text-xl">
            <el-icon>
              <Plus/>
            </el-icon>
          </div>
        </el-upload>
      </li>
    </ul>
  </div>
</template>
<script setup lang="ts">
import {useHTTP} from "@/plugins/http";
import {onMounted, ref, watch} from "vue";
import {Delete, Document, Download, Plus} from '@element-plus/icons-vue'
import type {UploadFile, UploadFiles, UploadRequestOptions} from 'element-plus'
import {ElMessage} from 'element-plus'
import {useI18n} from "vue-i18n";
import {useSettingsStore} from "@/stores/settingsStore";
import User from "@/models/user/User";
import UserAttachment from "@/models/user/UserAttachment";
import {Icon} from "@iconify/vue";
import {canBePreviewed, isImage, isPDF} from "@/lib/utils";
import {isUserAttachmentWithinLimit} from "@/lib/attachmentSize";

const props = withDefaults(defineProps<{
  data: User
  modelValue?: File[]
}>(), {
  modelValue: () => []
})

const emit = defineEmits<{
  (e: 'update:modelValue', files: File[]): void
}>()

const {t} = useI18n({
  messages: {
    en: {
      uploadAttachment: 'Upload Attachments',
      attachmentFileTooLarge: 'Attachment cannot exceed 5MB',
    },
    ar: {
      uploadAttachment: 'رفع مرفقات',
      attachmentFileTooLarge: 'يجب ألا يتجاوز حجم المرفق 5 ميغابايت',
    }
  }
})

const http = useHTTP()
const settingsStore = useSettingsStore();
const employee = ref<User>();
const currentAttachment = ref<UserAttachment>();
const stagedPreviewUrls = ref<Map<File, string>>(new Map())

onMounted(() => {
  employee.value = props.data as User
})

watch(() => props.data, (val) => {
  employee.value = val as User
})

watch(() => props.modelValue, (newFiles, oldFiles) => {
  if (oldFiles) {
    for (const f of oldFiles) {
      if (!newFiles.includes(f)) {
        const url = stagedPreviewUrls.value.get(f)
        if (url) {
          URL.revokeObjectURL(url)
          stagedPreviewUrls.value.delete(f)
        }
      }
    }
  }
  for (const f of newFiles) {
    if (!stagedPreviewUrls.value.has(f)) {
      stagedPreviewUrls.value.set(f, URL.createObjectURL(f))
    }
  }
})

function getStagedPreviewUrl(file: File): string {
  return stagedPreviewUrls.value.get(file) ?? ''
}

function removeStagedFile(index: number) {
  const updated = [...props.modelValue]
  updated.splice(index, 1)
  emit('update:modelValue', updated)
}

function beforeUpload(file: File) {
  const withinLimit = isUserAttachmentWithinLimit(file.size)
  if (!withinLimit) {
    ElMessage.error(t('attachmentFileTooLarge'))
  }
  return withinLimit
}

async function downloadAttachment(userAttachment: UserAttachment) {
  try {
    const response = await fetch(settingsStore.storagePath + '/users/' +
        employee.value?.id + '/' + userAttachment.filename);
    if (!response.ok) throw new Error('Network response was not ok');
    const blob = await response.blob();
    const urlObject = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = urlObject;
    link.download = userAttachment.originalFilename || 'downloaded-file';

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(urlObject);
  } catch (error) {
    console.error('Error downloading the file:', error);
  }
}

function handleRemoveOnRemote(uploadFile: UserAttachment) {
  http.delete(`${User.ENDPOINT}/attachments/${uploadFile.id}`).then(() => {
    onSuccess()
  })
}

function handleUpload(options: UploadRequestOptions) {
  if (!employee.value?.id) {
    emit('update:modelValue', [...props.modelValue, options.file as File])
    return Promise.resolve()
  }
  const data = new FormData();
  data.set('attachment', options.file)
  return http.put(options.action, data)
}

function showError(error: any, _uploadFile: UploadFile, _uploadFiles: UploadFiles) {
  ElMessage({
    showClose: true,
    message: error?.title,
    type: 'error',
  })
}

async function onSuccess() {
  if (!employee.value?.id) return
  employee.value = await http.get<User>(User.ENDPOINT +
      `/${employee.value?.id}`);
}

</script>
<style scoped>

</style>
